/// Maps the forecast's NWS-style icon codes ("skc.jpg", "nbkn.jpg", "shra40.jpg")
/// to a small set of drawn icons plus a plain-language label.

export type WeatherKind =
    | "clear"
    | "partly"
    | "mostly"
    | "overcast"
    | "fog"
    | "wind"
    | "drizzle"
    | "rain"
    | "showers"
    | "thunder"
    | "snow"
    | "sleet";

export interface WeatherSymbol {
    kind: WeatherKind;
    label: string;
    night: boolean;
}

const day = (kind: WeatherKind, label: string): WeatherSymbol => ({ kind, label, night: false });
const night = (kind: WeatherKind, label: string): WeatherSymbol => ({ kind, label, night: true });

// Keyed by code without extension. Codes ending in a number (shra40 = 40% chance)
// fall back to the code without it.
const SYMBOLS: Record<string, WeatherSymbol> = {
    skc: day("clear", "Clear"),
    nskc: night("clear", "Clear"),
    few: day("partly", "Mostly clear"),
    nfew: night("partly", "Mostly clear"),
    sct: day("partly", "Partly cloudy"),
    nsct: night("partly", "Partly cloudy"),
    bkn: day("mostly", "Mostly cloudy"),
    nbkn: night("mostly", "Mostly cloudy"),
    ovc: day("overcast", "Overcast"),
    novc: night("overcast", "Overcast"),
    fg: day("fog", "Fog"),
    nfg: night("fog", "Fog"),
    mist: day("fog", "Haze"),
    smoke: day("fog", "Smoke"),
    dust: day("fog", "Dust"),
    wind: day("wind", "Windy"),
    nwind: night("wind", "Windy"),
    ra1: day("drizzle", "Light rain"),
    nra: night("drizzle", "Light rain"),
    ra: day("rain", "Rain"),
    shra: day("rain", "Rain showers"),
    hi_shwrs: day("showers", "Showers nearby"),
    hi_nshwrs: night("showers", "Showers nearby"),
    tsra: day("thunder", "Thunderstorms"),
    ntsra: night("thunder", "Thunderstorms"),
    scttsra: day("thunder", "Scattered storms"),
    nscttsra: night("thunder", "Scattered storms"),
    hi_tsra: day("thunder", "Storms nearby"),
    hi_ntsra: night("thunder", "Storms nearby"),
    nsvrtsra: day("thunder", "Severe weather"),
    sn: day("snow", "Snow"),
    nsn: night("snow", "Snow"),
    fzra: day("sleet", "Freezing rain"),
    fzrara: day("sleet", "Freezing rain"),
    ip: day("sleet", "Ice pellets"),
    mix: day("sleet", "Wintry mix"),
    nmix: night("sleet", "Wintry mix"),
    raip: day("sleet", "Wintry mix"),
    rasn: day("sleet", "Rain and snow"),
    nrasn: night("sleet", "Rain and snow"),
};

/// null when the code is missing or not recognized
export function weatherSymbol(code: string | null | undefined): WeatherSymbol | null {
    if (!code) {
        return null;
    }
    const base = code.trim().toLowerCase().replace(/\.[^/.]+$/, "");
    return SYMBOLS[base] ?? SYMBOLS[base.replace(/[0-9]+$/, "")] ?? null;
}
